/**
 * useStudentHomeState — les gestes de l'accueil de l'élève.
 *
 * Ceux de l'onglet legacy (`TodoListScreen.js`, branche élève) et de son bandeau
 * (`NextLiveSessionBanner.js`) :
 *  - « GO ! » : `liveGoAnswer`, lu au toucher. Le jour du live, `pre-call` sur la séance du
 *    bandeau ; un autre jour, la boîte qui le dit (`not_today`) ; paiement en attente, la boîte de
 *    régularisation (`payment_blocked`), et la fermer relit les séances, comme
 *    `PaymentBlockedModal`. Quand ce qui est chargé ne dit pas « aujourd'hui », le compte est
 *    d'abord relu et « GO ! » répond sur les données fraîches, dans les 30 s et écran au premier
 *    plan (port de `useReloadBeforeRefusing`) ;
 *  - la mission de Sam : sa prochaine activité, avec Sam (`useLaunchStudentActivity`, point
 *    d'entrée `banner`), et les deux mesures du bandeau (`sam.banner.shown`, `.clicked`). Sans
 *    mission, « Étudie avec Sam en attendant » ouvre de même la première activité à faire, et ses
 *    mesures comptent les activités à faire, comme l'ancien bandeau (`prescribed_count`). Paiement
 *    en attente, son bouton ouvre la boîte de régularisation, comme « GO ! » : l'accueil d'un élève
 *    en impayé ne l'invite qu'à régulariser, son travail reste ouvert dans Activités (Wajih, 23/10 ;
 *    l'ancien bandeau le faisait même en mode Sam, sans mesure du clic) ;
 *  - le bandeau d'essai : sa route (`trial-school-subjects` / `trial-preparation-form`), sa croix ;
 *  - une ligne d'un live suivant répond comme « GO ! », pour SA séance : le jour du live, `pre-call` ;
 *    un autre jour, la boîte qui le dit, avec son jour (Wajih, 18/10 : la ligne ouvre la séance
 *    comme la carte du prochain live ; « Gérer », prévenir d'une absence, est sur « Tous mes lives ») ;
 *  - « Tous mes lives » ouvre la liste des lives (`live-sessions-list`) ;
 *  - le prochain contrôle ouvre « Mes contrôles », la quête « Mes quêtes », le rang le classement.
 */
import { useIsFocused, useNavigation } from "@react-navigation/native"
import { useCallback, useEffect, useReducer, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { apiRequest } from "../../../../../../data/redux/actions/api"
import { authService } from "../../../../../../data/services/authService"
import { liveService } from "../../../../../../data/services/liveService"
import { liveDayFromToday } from "../../../../../../data/utility/liveDay"
import { type ApiActivity } from "../../../../adapters/parent/Activities/activityAdapter"
import { findStudentActivity } from "../../../../adapters/student/Activities/studentActivitiesAdapter"
import { type StudentHomeViewModel } from "../../../../view-models/student/Home/studentHomeViewModel"
import { type UILiveSession } from "../../../types"
import { type UITodoRowItem } from "../../../types/studentHome"
import { openExternalUrl } from "../../../hooks/openExternalUrl"
import { useLaunchStudentActivity, type LaunchableActivity } from "../../../hooks/useLaunchStudentActivity"
import { LIVE_GO, liveGoAnswer } from "./liveGoAnswer"
import { initialStudentHomeUiState, studentHomeUiReducer, type StudentHomeNotice } from "./studentHomeUiState"
import { trackNotTodayClosed, trackSamBannerClicked, trackSamBannerShown } from "./studentHomeTracking"
import { selectDisplayedStudent } from "../../../../view-models/shared/displayedStudent"

/** Une relecture partie pour « GO ! » ne répond plus passé ce délai (`useReloadBeforeRefusing`). */
const GO_ANSWER_WINDOW_MS = 30 * 1000

type HomeNavigation = { push: (route: string, params?: object) => void; navigate: (route: string) => void }

/** La séance à laquelle « GO ! » répond : la prochaine, ou celle d'une ligne touchée. */
type GoTarget = { session: UILiveSession; liveSessionId: string | number | null }

export function useStudentHomeState(vm: StudentHomeViewModel) {
    const dispatch = useDispatch()
    const navigation = useNavigation() as unknown as HomeNavigation
    const isFocused = useIsFocused()
    const launch = useLaunchStudentActivity()
    const [ui, dispatchUi] = useReducer(studentHomeUiReducer, initialStudentHomeUiState)

    const todoActivities = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => selectDisplayedStudent(state)?.todo_activities as ApiActivity[] | undefined
    )
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const account = useSelector((state: any) => selectDisplayedStudent(state) as unknown)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const reloadingAccount = useSelector((state: any) => !!state?.api?.getUser?.request)

    // « GO ! » répond sur ce qui est chargé AU MOMENT où il répond, relecture comprise.
    const vmRef = useRef(vm)
    vmRef.current = vm
    const goPressRef = useRef<{ at: number; account: unknown; target: GoTarget | null } | null>(null)
    // La séance dont la boîte parle : la prochaine, ou celle de la ligne touchée.
    const [noticeSession, setNoticeSession] = useState<UILiveSession | null>(null)

    const openNotice = useCallback((notice: StudentHomeNotice, session: UILiveSession | null) => {
        setNoticeSession(session)
        dispatchUi({ type: "OPEN_NOTICE", notice })
    }, [])

    const answerGo = useCallback(
        (canReload: boolean, target: GoTarget | null = null) => {
            const current = vmRef.current
            // Une ligne touchée garde SA séance ; « GO ! » lit la prochaine au moment où il répond.
            const goal: GoTarget | null =
                target ??
                (current.nextSession != null
                    ? { session: current.nextSession, liveSessionId: current.nextLiveSessionId }
                    : null)
            const answer = liveGoAnswer({
                liveDay: liveDayFromToday(goal?.session.date) as string | null,
                paymentBlocked: current.paymentBlocked,
                canReload,
            })
            switch (answer) {
                case LIVE_GO.PAYMENT_BLOCKED:
                    return openNotice("payment_blocked", goal?.session ?? null)
                case LIVE_GO.JOIN_LIVE:
                    return navigation.push("pre-call", { liveSessionId: goal?.liveSessionId })
                case LIVE_GO.RELOAD:
                    goPressRef.current = { at: Date.now(), account, target }
                    dispatch(apiRequest(authService.getUser) as never)
                    return
                case LIVE_GO.REFUSE:
                    return openNotice("not_today", goal?.session ?? null)
            }
        },
        [account, dispatch, navigation, openNotice]
    )

    // La relecture partie pour « GO ! » est revenue : on répond, une fois, sur les données fraîches.
    useEffect(() => {
        const press = goPressRef.current
        if (press == null || reloadingAccount) return
        if (Date.now() - press.at > GO_ANSWER_WINDOW_MS) {
            goPressRef.current = null
            return
        }
        // Pas encore relu, ou la relecture a échoué : le délai tranchera.
        if (account === press.account || !isFocused) return
        goPressRef.current = null
        answerGo(false, press.target)
    }, [account, answerGo, isFocused, reloadingAccount])

    const closeNotice = useCallback(
        (how?: "button" | "dismiss") => {
            if (ui.notice === "payment_blocked") dispatch(apiRequest(liveService.getAllMyNextLiveSessions) as never)
            if (ui.notice === "not_today") trackNotTodayClosed(how)
            dispatchUi({ type: "CLOSE_NOTICE" })
        },
        [dispatch, ui.notice]
    )

    // La mission a été vue : une mesure par apparition, comme le bandeau de Sam legacy. Sans mission,
    // le compte mesuré est celui de l'ancien bandeau : les activités à faire.
    const mission = vm.samMission
    const trackedCount = mission?.kind === "study" ? (todoActivities?.length ?? 0) : mission?.total
    const missionShownRef = useRef(false)
    useEffect(() => {
        if (mission != null && !missionShownRef.current) {
            missionShownRef.current = true
            trackSamBannerShown(trackedCount ?? 0)
        }
        if (mission == null) missionShownRef.current = false
    }, [mission, trackedCount])

    const rawActivity = useCallback(
        (id: string | number): LaunchableActivity =>
            findStudentActivity({ todo_activities: todoActivities }, id) ?? { id },
        [todoActivities]
    )

    const startSamMission = useCallback(() => {
        if (vmRef.current.paymentBlocked) return openNotice("payment_blocked", vmRef.current.nextSession)
        const activityId = mission?.nextActivityId
        if (activityId == null) return
        trackSamBannerClicked(trackedCount, activityId)
        launch(rawActivity(activityId), "banner")
    }, [launch, mission, openNotice, rawActivity, trackedCount])

    return {
        trialTodo: ui.trialDismissed ? null : vm.trialTodo,
        onTrialTodoPress: (todo: UITodoRowItem) => {
            if (todo.route) navigation.push(todo.route, todo.routeParams)
        },
        onDismissTrialTodo: () => dispatchUi({ type: "DISMISS_TRIAL" }),
        onJoinSession: () => answerGo(true),
        onSelectFollowingLive: (session: UILiveSession) =>
            answerGo(true, { session, liveSessionId: session.liveSessionId ?? null }),
        onOpenAllLives: () => navigation.push("live-sessions-list"),
        notice: ui.notice,
        noticeSession,
        closeNotice,
        onSettlePayment: vm.paymentUrl ? () => openExternalUrl(vm.paymentUrl) : undefined,
        onStartSamMission: startSamMission,
        onOpenAssessments: () => navigation.navigate("school-assessments"),
        onOpenQuests: () => navigation.push("skill-set"),
        onOpenRanking: () => navigation.push("ranking"),
    }
}
